import React, { useEffect, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { ChevronLeftIcon } from '@heroicons/react/24/outline';
import EditFilledIcon from 'assets/edit_filled_icon.svg?react';
import void_ava from 'assets/gap_avatar.svg';
import InfoChip from 'assets/info_gray_chip.svg?react';
import PlusFilledIcon from 'assets/plus_filled_icon.svg?react';
import TrashFilledIcon from 'assets/trash_filled_icon.svg?react';
import { SSEEventType } from 'constants/sse';
import useSSEEvent from 'hooks/useSSEEvent';
import styled from 'styled-components';
import { css, keyframes } from 'styled-components';
import { successAlert } from 'ui-kit/Alert';
import Spinner from 'ui-kit/Spinner';

import UserMessageTool from '../../ai/tools/UserMessageTool';
import NiceConfirm from '../../modals/NiceConfirm';
import NiceModal from '../../modals/NiceModal';
import PictureModal from '../../modals/PictureModal';
import { CreateMessageTool } from './CreateMessageTool';

export const InitialMessagesTool = (props) => {
  const {
    messages = [],
    charactersAvatars = {},
    charactersNames = [],
    playerName = '',
    onBack = Function.prototype,
    onContinue = Function.prototype,
    onChange = Function.prototype
  } = props;

  const [initialMessages, setInitialMessages] = useState(messages);
  const [addMessageModal, setAddMessageModal] = useState(undefined);
  const [editMessageModal, setEditMessageModal] = useState(undefined);
  const [deleteMessageModal, setDeleteMessageModal] = useState(undefined);
  const [loading, setLoading] = useState(false);
  const [pictureModal, setPictureModal] = useState(undefined);
  const [avatarRefreshKeys, setAvatarRefreshKeys] = useState({});
  const [avatarErrors, setAvatarErrors] = useState({});

  const { data: avatarGenerationData } = useSSEEvent(SSEEventType.AVATAR_GENERATED);

  const allCharacters = ['Narrator', playerName, ...charactersNames];

  useEffect(() => {
    onChange(initialMessages);
  }, [initialMessages]);

  useEffect(() => {
    if (avatarGenerationData?.status === 'completed') {
      setAvatarRefreshKeys(prev => {
        const result = { ...prev };
        Object.keys(charactersAvatars).forEach(key => {
          result[key] = (result[key] || 0) + 1;
        });
        return result;
      });
    }
  }, [avatarGenerationData]);

  const handleImageError = (e, key) => {
    e.target.src = void_ava;
    setAvatarErrors(prev => ({ ...prev, [key]: true }));
  };



  return (
    <Wrapper>
      <Label>Initial messages</Label>
      {initialMessages?.map((item, index) => {
        const refreshKey = avatarRefreshKeys[item.name] || 0;
        const isGapAvatar = charactersAvatars[item.name] === 'gap';
        const isNarrator = item.name === 'Narrator';
        let message;
        if (isNarrator) {
          message = <Environment>{item.text}</Environment>;
        } else if (item?.name?.toLowerCase() === playerName?.toLowerCase()) {
          message =
            <div className='flex flex-col items-end gap-2'>
              <User>You</User>
              <UserMessage>
                <UserMessageTool text={item.text} />
              </UserMessage>
            </div>;
        } else {
          message =
            <div className='flex items-start gap-2'>
              <AvatarWrapper hasError={avatarErrors[item.name] && !isGapAvatar}>
                <img
                  key={`${item.name}-avatar-${refreshKey}`}
                  src={charactersAvatars[item.name]}
                  alt={'avatar'}
                  className={`w-full h-full ${isGapAvatar ? 'cursor-default' : 'cursor-pointer'} object-cover`}
                  onClick={() => {
                    const isGapAvatar = charactersAvatars[item.name] === 'gap';
                    if (!isGapAvatar) {
                      setPictureModal(charactersAvatars[item.name]);
                    }
                  }}
                  onError={(e) => handleImageError(e, item.name)}
                />
              </AvatarWrapper>
              <div className='flex flex-col gap-2'>
                <CharacterName>
                  {item.name}
                </CharacterName>
                <CharacterMessage>
                  <UserMessageTool text={item.text} />
                </CharacterMessage>
              </div>
            </div>;
        }
        return (
          <MessageContainer key={item?.text + index}>
            <div className="flex-grow">
              {message}
            </div>
            <ActionIcons isNarrator={isNarrator}>
              <EditFilledIcon
                onClick={() => setEditMessageModal({ ...item, index })}
              />
              {index !== 0 && (
                <TrashFilledIcon
                  onClick={() => setDeleteMessageModal(index)}
                />
              )}
              <PlusFilledIcon
                onClick={() => setAddMessageModal(index)}
              />
            </ActionIcons>
          </MessageContainer>
        );
      })}
      <Divider />

      <Info><InfoChip />You can delete or edit the message. Or add a new message</Info>

      <ButtonPlaceholder>
        {isMobile && (
          <MobileBackButton
            type="button"
            onClick={onBack}
          >
            <ChevronLeftIcon />
          </MobileBackButton>
        )}
        {!isMobile && (
          <TransparentButton
            type="button"
            onClick={onBack}
          >
            Back
          </TransparentButton>
        )}

        <OrangeButton isMobile={isMobile} onClick={async () => {
          if (loading) {
            return;
          }
          setLoading(true);
          await onContinue(initialMessages);
          setLoading(false);
        }}>
          {loading ? <Spinner /> : 'Continue'}
        </OrangeButton>
      </ButtonPlaceholder>

      {addMessageModal !== undefined && (
        <NiceModal zIndex={210} onClose={() => {
          setAddMessageModal(undefined);
        }}>
          <InnerModal>
            <CreateMessageTool
              allCharacters={allCharacters}
              isEditing={false}
              onCancel={() => setAddMessageModal(undefined)}
              onSave={(d) => {
                const data = {
                  name: d.name,
                  role: "assistant",
                  text: d.text,
                };
                const newMessages = initialMessages.slice(0, addMessageModal + 1).concat(data).concat(initialMessages.slice(addMessageModal + 1));
                setInitialMessages(newMessages);
                successAlert('Added');
                setAddMessageModal(undefined);
              }}
            />
          </InnerModal>
        </NiceModal>
      )}

      {editMessageModal && (
        <NiceModal zIndex={210} onClose={() => {
          setEditMessageModal(undefined);
        }}>
          <InnerModal>
            <CreateMessageTool
              allCharacters={allCharacters}
              text={editMessageModal.text}
              name={editMessageModal.name}
              avatar={charactersAvatars[editMessageModal.name]}
              isEditing={true}
              onCancel={() => setEditMessageModal(undefined)}
              onSave={(d) => {
                const data = {
                  name: editMessageModal.name,
                  role: "assistant",
                  text: d,
                };
                const index = editMessageModal.index;
                const newMessages = initialMessages.slice(0, index).concat(data).concat(initialMessages.slice(index + 1));
                setInitialMessages(newMessages);
                successAlert('Saved');
                setEditMessageModal(undefined);
              }}
            />
          </InnerModal>
        </NiceModal>
      )}

      {deleteMessageModal && (
        <NiceConfirm
          heading="Delete Message"
          subHeading="Are you sure you want to delete this message?"
          cancelText='Cancel'
          confirmText='Delete'
          onConfirm={() => {
            const newMessages = initialMessages.slice(0, deleteMessageModal).concat(initialMessages.slice(deleteMessageModal + 1));
            setInitialMessages(newMessages);
            successAlert('Deleted');
            setDeleteMessageModal(undefined);
          }}
          onCancel={() => setDeleteMessageModal(undefined)}
        />
      )}

      {pictureModal && (
        <PictureModal onClose={() => setPictureModal(undefined)} src={pictureModal} />
      )}
    </Wrapper>
  );
};

const Wrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 24px;
  @media (max-width: 768px) {
    margin-bottom: 80px;
  }
`;

const Divider = styled.div`
  width: 100%;
  height: 1px;
  background-color: #686868;
`;

const Label = styled.span`
  font-family: Nunito Sans;
  font-weight: 700;
  font-size: 16px;
  line-height: 18px;
`;

const Environment = styled.div`
    color: rgba(169, 169, 169, 1);
    font-family: Nunito Sans;
    font-size: 16px;
    font-style: italic;
    font-weight: 400;
    line-height: 18px;
`;

const UserMessage = styled.div`
  text-align: right;
  background-color: rgba(70, 55, 50, 1);
  padding: 16px;
  border-radius: 12px;
  border-top-right-radius: 4px;
  display: flex;
  flex-direction: column;
`;

const Info = styled.span`
  font-family: Nunito Sans;
  font-size: 14px;
  font-weight: 400;
  line-height: 16px;
  color: #A9A9A9;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const User = styled.span`
  color: rgba(255, 126, 85, 1);
  font-family: Nunito Sans;
  font-size: 14px;
  font-weight: 400;
  line-height: 16px;
  text-align: right;
`;

const CharacterName = styled.div`
  font-family: Nunito Sans;
  font-size: 16px;
  font-weight: 400;
  line-height: 18px;
  text-align: left;
  color: white;
`;

const CharacterMessage = styled.div`
  background-color: rgba(19, 19, 19, 1);
  padding: 16px;
  border-radius: 12px;
  border-top-left-radius: 4px;
  display: flex;
  flex-direction: column;
`;

const InnerModal = styled.div`
  padding: 20px;
  width: 380px;
  display: flex;
  flex-direction: column;
  gap: 24px;

    @media (max-width: 400px) {
        width: 100%;
    }
`;

const ButtonPlaceholder = styled.div`
    padding-top: 8px;
    width: 100%;
    display: flex;
    gap: 10px;
    
    @media (max-width: 768px) {
      margin-top: auto;
      background: #1A1A1A;
      padding-top: 10px;
      padding-bottom: 16px;
      bottom: 0;
      position: fixed;
      width: 100%;
      z-index: 10;
    }
`;

const OrangeButton = styled.button`
    background-color: rgba(255, 126, 85, 1);
    width: ${props => props.isMobile ? '66%' : '100%'};
    padding: 8px 24px;
    border-radius: 100px;
    font-family: Nunito Sans;
    font-size: 18px;
    font-weight: 700;
    line-height: 24px;
    box-shadow: 0px 12px 40px 0px rgba(255, 126, 85, 0.12);
    display: flex;
    justify-content: center;
`;

const TransparentButton = styled(OrangeButton)`
    background-color: transparent;
    border: 1px solid rgba(255, 126, 85, 1);
    color: rgba(255, 126, 85, 1);
    box-shadow: none;
`;

const MobileBackButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 40px;
  background: #1A1A1A;
  border-radius: 100px;
  color: white;
  border: 1px solid #FF7E55;
  cursor: pointer;
  padding: 0;
  
  svg {
    width: 24px;
    height: 24px;
  }
`;

const shimmerAnimation = keyframes`
  0% {
    transform: translate(-100%, -100%) rotate(45deg);
    opacity: 0;
  }
  5% {
    opacity: 1;
  }
  47% {
    transform: translate(100%, 100%) rotate(45deg);
    opacity: 1;
  }
  53% {
    transform: translate(100%, 100%) rotate(45deg);
    opacity: 1;
  }
  95% {
    transform: translate(-100%, -100%) rotate(45deg);
    opacity: 1;
  }
  100% {
    transform: translate(-100%, -100%) rotate(45deg);
    opacity: 0;
  }
`;

const AvatarWrapper = styled.div`
  position: relative;
  min-width: 54px;
  width: 54px;
  height: 54px;
  border-radius: 100px;
  overflow: hidden;
  
  ${props => props.hasError && css`
    background: #131313;
    
    &::after {
      content: '';
      position: absolute;
      top: -50%;
      left: -50%;
      width: 200%;
      height: 200%;
      background: linear-gradient(
        90deg,
        transparent 0%,
        transparent 40%,
        rgba(255, 255, 255, 0.04) 47%,
        rgba(255, 255, 255, 0.07) 50%,
        rgba(255, 255, 255, 0.04) 53%,
        transparent 60%,
        transparent 100%
      );
      transform: translate(-100%, -100%) rotate(45deg);
      animation: ${shimmerAnimation} 4s cubic-bezier(0.4, 0.0, 0.2, 1) infinite;
    }
  `}
`;

const MessageContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  width: 100%;
  gap: 16px;
`;

const ActionIcons = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-top: 30px;
  
  ${props => props.isNarrator && css`
    padding-top: 0px;
  `}
`;